import React, { useEffect, useState } from "react";
import { Form, Row, Col, Checkbox, message, Alert } from "antd";
import { SelectValue } from "antd/lib/select";
import { useTranslation } from "react-i18next";
import { storeUserData, storeCredentials, getCredentials } from "utils/auth";

import StyledButton from "common/button";
import UPDATE_PROFILE from "graphql/mutations/UpdateProfile";
import GET_MY_SETTINGS from "graphql/queries/GetMySettings";
import STUDENT_RESET_PASSWORD from "graphql/mutations/StudentResetPassword";
import GET_LANGUAGES from "graphql/queries/GetLanguages";

import { useMutation, useQuery } from "@apollo/client";

import {
  TIME_FORMATS,
  CLASS_TYPES,
  NOTIFICATION_METHOD,
} from "utils/constants";
import {
  filterActiveLevelsAndCurrentUserLevel,
  getLevelId,
  setIsChineseLanguage,
} from "utils/language";
import * as StyledForm from "pages/account/AccountFormStyles";
import { TimeZone, Language, Level } from "types";
import { setLanguageDataVar } from "cache";

const parseFormValues = (values: any) => {
  return {
    ...values,
    preference: "simplifiedCharacters", // set by default
    currentLevelId: values.currentLevel.id,
    notificationMethod: values.notificationMethod,
    currentLanguageId: values.currentLanguage.id,
  };
};

const formatStoreValues = (values: any) => {
  const {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    preference, // remove it from form value before submitting
    ...rest
  } = values;

  return {
    ...rest,
  };
};

const MySettingsForm = () => {
  const { i18n, t } = useTranslation("my_settings");
  // use to show specify field for chinese language
  const [isShowSpecifyChinese, setIsShowSpecifyChinese] = useState(true);

  // use to get study language's levels
  const [studyLanguageId, setStudyLanguageId] = useState("");

  const [updatePasswordForm] = Form.useForm();
  const [updateMySettingsForm] = Form.useForm();

  const { data, loading } = useQuery(GET_MY_SETTINGS, {
    fetchPolicy: "network-only",
  });

  const { data: languageList } = useQuery(GET_LANGUAGES);
  const [updateMySettings] = useMutation(UPDATE_PROFILE, {
    refetchQueries: [{ query: GET_MY_SETTINGS }],
    onCompleted: () => {
      message.success(t("Changes saved successfully!"));
    },
    onError: () => {
      message.error(t("Failed saving changes. Please try again."));
    },
  });

  useEffect(() => {
    if (data?.me && languageList?.languages) {
      // Need to parse any date field from string to Moment object
      updateMySettingsForm.setFieldsValue(parseFormValues(data?.me));
      // TODO get current study language from userInfo
      setIsShowSpecifyChinese(
        setIsChineseLanguage(data.me.currentLanguage.id, languageList.languages)
      );
      setStudyLanguageId(data?.me.currentLanguage.id);
    }
  }, [data, updateMySettingsForm, languageList]);

  const handleSubmitMySettingsForm = async (values: any) => {
    const { data: mySettingsData } = await updateMySettings({
      variables: {
        input: {
          attributes: formatStoreValues(values),
        },
      },
    });

    const {
      updateMe: {
        me: { locale },
        credentials,
      },
    } = mySettingsData;
    i18n.changeLanguage(locale);
    storeCredentials(credentials);
    storeUserData({ locale });
    setLanguageDataVar({ studyLanguageId: values.currentLanguageId });
  };

  const [resetPassword] = useMutation(STUDENT_RESET_PASSWORD);

  const handleChangePassword = async () => {
    const userCredential = getCredentials();
    try {
      const {
        data: {
          studentSendPasswordResetWithToken: { message: successMessage },
        },
      } = await resetPassword({
        variables: {
          email: userCredential.uid,
          redirectUrl: `${window.location.origin}/reset-password-confirm`,
        },
      });

      message.success(successMessage);
    } catch (e) {
      message.error((e as Error).message);
    }
  };

  const onStudyLanguageChange = (value: string) => {
    const levelId = getLevelId(
      value,
      languageList.languages,
      data.me.savedLevels?.find(
        (level: { id: string; languageId: string }) =>
          level.languageId === value
      )?.id || data.me.currentLevel.id
    );

    const isChineseLanguage = setIsChineseLanguage(
      value,
      languageList.languages
    );
    if (!levelId) {
      return setStudyLanguageId((prevStudyLanguageId: string) => {
        updateMySettingsForm.setFields([
          {
            name: "currentLanguageId",
            value: prevStudyLanguageId,
          },
        ]);
        return prevStudyLanguageId;
      });
    }

    if (isChineseLanguage) {
      updateMySettingsForm.setFields([
        {
          name: "currentLevelId",
          value: levelId,
        },
      ]);
    } else {
      updateMySettingsForm.setFields([
        {
          name: "currentLevelId",
          value: levelId,
        },
        {
          name: "charactersPreference",
          value: "simplified",
        },
        {
          name: "spokenOnly",
          value: false,
        },
      ]);
    }

    setIsShowSpecifyChinese(isChineseLanguage);
    return setStudyLanguageId(value);
  };

  if (loading) return <Alert type="info" message={t("Loading...")} />;
  if (!data) return null;

  const { locales: availableLocales, timeZones } = data;

  const studyLanguage = languageList?.languages.find((language: Language) => {
    return language.id === studyLanguageId;
  });

  const levelOptions = filterActiveLevelsAndCurrentUserLevel(
    data?.me?.currentLevel?.id,
    studyLanguage?.levels
  );

  const renderPasswordFormGroup = () => (
    <StyledForm.Group>
      <StyledForm.GroupLabel>{t("PASSWORD")}</StyledForm.GroupLabel>
      <StyledForm.Content>
        <StyledForm.GroupedRow gutter={{ xs: 8, md: 16 }}>
          <Col xs={24} md={12}>
            <StyledButton
              style={{ width: "100%", marginTop: 16 }}
              htmlType="submit"
            >
              {t("CHANGE MY PASSWORD")}
            </StyledButton>
          </Col>
        </StyledForm.GroupedRow>
      </StyledForm.Content>
    </StyledForm.Group>
  );

  const renderDisplayLanguageFormGroup = () => (
    <StyledForm.Group>
      <StyledForm.GroupLabel>{t("DISPLAY LANGUAGE")}</StyledForm.GroupLabel>
      <StyledForm.Content>
        <Row gutter={{ xs: 8, md: 16 }}>
          <Col xs={24} md={12}>
            <StyledForm.Item name="locale">
              <StyledForm.Select showArrow>
                {availableLocales.map(
                  (locale: { code: string; language: string }) => (
                    <StyledForm.Select.Option
                      value={locale.code}
                      key={locale.code}
                    >
                      {locale.language}
                    </StyledForm.Select.Option>
                  )
                )}
              </StyledForm.Select>
            </StyledForm.Item>
          </Col>
        </Row>
      </StyledForm.Content>
    </StyledForm.Group>
  );

  const renderLearningFormGroup = () => (
    <StyledForm.Group>
      <StyledForm.GroupLabel>
        {t("LANGUAGE I AM LEARNING")}
      </StyledForm.GroupLabel>
      <StyledForm.Content>
        <Row gutter={{ xs: 8, md: 16 }}>
          <Col xs={24}>
            <StyledForm.Item name="currentLanguageId">
              <StyledForm.Select
                showArrow
                loading={loading}
                onChange={(value: SelectValue) => {
                  onStudyLanguageChange(value as string);
                }}
              >
                {languageList?.languages?.map((language: Language) => (
                  <StyledForm.Select.Option
                    value={language.id.toString()}
                    key={language.id}
                  >
                    {language.englishName} {language.realName}
                  </StyledForm.Select.Option>
                ))}
              </StyledForm.Select>
            </StyledForm.Item>
          </Col>
        </Row>
        <br />
        <Row gutter={{ xs: 8, md: 16 }}>
          <Col xs={24} md={12}>
            <StyledForm.Item name="currentLevelId" label={t("My Level")}>
              <StyledForm.Select showArrow loading={loading} value={undefined}>
                {levelOptions.map((level: Level) => (
                  <StyledForm.Select.Option
                    value={level.id}
                    key={level.id}
                    disabled={!level.active}
                  >
                    {level.title}
                  </StyledForm.Select.Option>
                ))}
              </StyledForm.Select>
            </StyledForm.Item>
          </Col>
          <Col xs={24} md={12}>
            <StyledForm.Item name="preferClassType" label={t("Class Type")}>
              <StyledForm.Select showArrow>
                {CLASS_TYPES.map(
                  (classType: { code: number; name: string }) => (
                    <StyledForm.Select.Option
                      value={classType.code}
                      key={classType.code}
                    >
                      {classType.name}
                    </StyledForm.Select.Option>
                  )
                )}
              </StyledForm.Select>
            </StyledForm.Item>
          </Col>
        </Row>
        <br />

        <>
          <Row
            gutter={{ xs: 8, md: 16 }}
            style={isShowSpecifyChinese ? {} : { display: "none" }}
          >
            <Col xs={24}>
              <StyledForm.Item name="charactersPreference">
                <StyledForm.Select showArrow>
                  <StyledForm.Select.Option value="simplified">
                    {t("I want to learn Simplified Characters")}
                  </StyledForm.Select.Option>
                  <StyledForm.Select.Option value="traditional">
                    {t("I want to learn Traditional Characters")}
                  </StyledForm.Select.Option>
                </StyledForm.Select>
              </StyledForm.Item>
            </Col>
          </Row>
          <br />
          <Row
            gutter={{ xs: 8, md: 16 }}
            style={isShowSpecifyChinese ? {} : { display: "none" }}
          >
            <Col xs={24}>
              <StyledForm.Item name="spokenOnly" valuePropName="checked">
                <Checkbox name="spokenOnly">
                  {t("I study only spoken Chinese (no characters)")}
                </Checkbox>
              </StyledForm.Item>
            </Col>
          </Row>
        </>
      </StyledForm.Content>
    </StyledForm.Group>
  );

  const renderTimeSetupFormGroup = () => (
    <StyledForm.Group>
      <StyledForm.GroupLabel>{t("TIME SETUP")}</StyledForm.GroupLabel>
      <StyledForm.Content>
        <Row gutter={{ xs: 8, md: 16 }}>
          <Col xs={24} md={16}>
            {/* If you notice something weird, see this PR desc
            https://github.com/LTLOnline/flexi/pull/721 */}
            <StyledForm.Item
              style={{ marginTop: 8 }}
              name="timeZone"
              label={t("Time Zone")}
            >
              <StyledForm.Select showArrow showSearch>
                {timeZones.map((timeZone: TimeZone) => (
                  <StyledForm.Select.Option
                    value={timeZone.name}
                    key={timeZone.name}
                  >
                    {timeZone.nameWithOffset}
                  </StyledForm.Select.Option>
                ))}
              </StyledForm.Select>
            </StyledForm.Item>
          </Col>
          <Col sm={{ span: 24, offset: 0 }} md={8}>
            <StyledForm.Item
              style={{ marginTop: 8 }}
              name="timeFormat"
              label={t("Time Format")}
            >
              <StyledForm.Select showArrow>
                {TIME_FORMATS.map(
                  // eslint-disable-next-line react/no-unused-prop-types
                  ({ code, format }: { code: string; format: string }) => (
                    <StyledForm.Select.Option value={code} key={code}>
                      {format}
                    </StyledForm.Select.Option>
                  )
                )}
              </StyledForm.Select>
            </StyledForm.Item>
          </Col>
        </Row>
      </StyledForm.Content>
    </StyledForm.Group>
  );

  const renderMailboxFormGroup = () => (
    <StyledForm.Group>
      <StyledForm.GroupLabel>{t("MAILBOX")}</StyledForm.GroupLabel>
      <StyledForm.Content>
        <Row gutter={{ xs: 8, md: 16 }}>
          <Col xs={24}>
            <StyledForm.Item name="notificationMethod">
              <StyledForm.Select showArrow>
                <StyledForm.Select.Option
                  value={NOTIFICATION_METHOD.MAILBOX_AND_EMAIL}
                >
                  {t("Send me notifications to My Mailbox and Email")}
                </StyledForm.Select.Option>
                <StyledForm.Select.Option
                  value={NOTIFICATION_METHOD.MAILBOX_ONLY}
                >
                  {t("Send me notifications to My Mailbox only")}
                </StyledForm.Select.Option>
              </StyledForm.Select>
            </StyledForm.Item>
          </Col>
        </Row>
        <br />
        <Row gutter={{ xs: 8, md: 16 }}>
          <Col xs={24}>
            <StyledForm.Item name="remindNextClass" valuePropName="checked">
              <Checkbox name="remindNextClass">
                {t("Next class schedule and reminder.")}
              </Checkbox>
            </StyledForm.Item>
          </Col>
        </Row>
        <Row gutter={{ xs: 8, md: 16 }}>
          <Col xs={24}>
            <StyledForm.Item name="remindRateClass" valuePropName="checked">
              <Checkbox name="remindRateClass">
                {t("Reminder to rate my past classes.")}
              </Checkbox>
            </StyledForm.Item>
          </Col>
        </Row>
        <Row gutter={{ xs: 8, md: 16 }}>
          <Col xs={24}>
            <StyledForm.Item name="sendTips" valuePropName="checked">
              <Checkbox name="sendTips">
                {t("Tips, updates, and offers to help me study emails.")}
              </Checkbox>
            </StyledForm.Item>
          </Col>
        </Row>
      </StyledForm.Content>
    </StyledForm.Group>
  );

  return (
    <StyledForm.Container>
      <Form
        name="updatePassword"
        form={updatePasswordForm}
        layout="vertical"
        onFinish={handleChangePassword}
      >
        {renderPasswordFormGroup()}
      </Form>
      <Form
        name="updateMySettings"
        form={updateMySettingsForm}
        layout="vertical"
        onFinish={handleSubmitMySettingsForm}
      >
        {renderDisplayLanguageFormGroup()}
        {renderLearningFormGroup()}
        {renderTimeSetupFormGroup()}
        {renderMailboxFormGroup()}
        <StyledButton htmlType="submit">{t("SAVE CHANGES")}</StyledButton>
      </Form>
    </StyledForm.Container>
  );
};

export default MySettingsForm;
